import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { Alert, AlertTitle } from "@mui/material";
import { useConfiguration } from "./hooks/useConfiguration";
import { usePropertyId } from "./hooks/usePropertyId";
import { setPropertyConfig } from "../redux/configSlice";
import { ConfigService } from "../services/ConfigService";
import LoadingScreen from "./LoadingScreen";

const ConfigProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { appConfig, isLoadingConfiguration, isConfigurationError } = useConfiguration();
    
    // Load property config directly using useQuery once static config is available
    const configService = appConfig ? new ConfigService(appConfig.apiBaseUrl) : null;
    
    const { 
        data: propertyConfig,
        isLoading: isLoadingPropertyConfig, 
        isError: isPropertyConfigError, 
    } = useQuery({
        queryKey: ['propertyConfig'],
        queryFn: () => configService.getConfig(),
        enabled: !!appConfig,  // Only run when staticConfig is available
        staleTime: Infinity,  // Never refetch - config is immutable for the session
        cacheTime: Infinity,
        retry: 3
    });

    // Dispatch property config to Redux when loaded
    useEffect(() => {
        if (propertyConfig && !isLoadingPropertyConfig && !isPropertyConfigError) {
            dispatch(setPropertyConfig(propertyConfig));
        }
    }, [propertyConfig, isLoadingPropertyConfig, isPropertyConfigError, dispatch]);

    // Get propertyId from Redux to check if it's loaded
    const propertyId = usePropertyId();

    // Show loading screen while actively loading
    if (isLoadingConfiguration || isLoadingPropertyConfig) {
        return <LoadingScreen />;
    }

    // Show error if configs failed OR if loading finished but data is missing
    if (isConfigurationError || isPropertyConfigError || !appConfig || !propertyId) {
        return <ConfigurationError />;
    }

    return children;
}

const ConfigurationError = () => (
    <Alert severity="error">
        <AlertTitle>Application Unavailable</AlertTitle>
        <div>
            The application could not be loaded. Please try again later.
        </div>
    </Alert>
);

export default ConfigProvider;